/**
 * BuzzBit X — Connect Claude documentation.
 *
 * Public static page rendered at /docs/claude. No markdown library — the
 * content is laid out as native JSX so the page is fast to load, indexable
 * by search engines, and free of runtime parsing.
 *
 * Sidebar navigation jumps to in-page sections via hash anchors. The
 * layout is intentionally minimal — utility-first Tailwind classes only,
 * no design-system primitives the docs route shouldn't pull in.
 *
 * The section bodies live in the sibling ./claudeDocsParts/ modules; this
 * file is the page shell (header + sidebar + intro + footer).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/brand/BrandLogo';

import { SECTIONS } from './claudeDocsParts/data';
import { t } from '../../i18n';
import { SetupSections } from './claudeDocsParts/SetupSections';
import { ReferenceSections } from './claudeDocsParts/ReferenceSections';

const ClaudeDocs: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 pb-cookie-banner [--pb-cookie-base:0px]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center min-w-0">
            <BrandLogo size="sm" />
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/docs/claude" className="text-gray-900 font-medium">
              Docs
            </Link>
            <a
              href="https://github.com/arad1do/buzzbit-cli"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-900"
            >
              CLI repo
            </a>
            <a
              href="https://github.com/arad1do/buzzbit-claude-plugin"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-900"
            >
              Plugin repo
            </a>
            <Link
              to="/settings/integrations"
              className="px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-gray-800 text-white"
            >
              Open Settings
            </Link>
          </nav>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8">
        <aside className="md:sticky md:top-8 md:self-start">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 mb-3">
            Connect Claude
          </h3>
          <nav className="space-y-1">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block px-2 py-1 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        <main className="min-w-0">
          <h1 className="text-3xl font-semibold text-gray-900 mb-2 break-words">Connect Claude to BuzzBit X</h1>
          <p className="text-gray-600 mb-10">{t('docs.hero.intro')}</p>

          <SetupSections />
          <ReferenceSections />

          <footer className="border-t border-gray-200 mt-16 pt-6 text-xs text-gray-500">
            Need help?{' '}
            <a
              href="mailto:support@buzzbitx.com"
              className="text-gray-900 underline"
            >
              support@buzzbitx.com
            </a>{' '}
            · Issues:{' '}
            <a
              href="https://github.com/arad1do/buzzbit-cli/issues"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 underline"
            >
              CLI
            </a>{' '}
            ·{' '}
            <a
              href="https://github.com/arad1do/buzzbit-claude-plugin/issues"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-900 underline"
            >
              Plugin
            </a>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default ClaudeDocs;
